"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { getTenantSummary } from "@/services/api";
import type { TenantSummary } from "@/services/api";

type TenantSummaryContextValue = {
  summary: TenantSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const TenantSummaryContext = createContext<TenantSummaryContextValue | null>(null);

export function TenantSummaryProvider({ children }: { children: ReactNode }) {
  const [summary, setSummary] = useState<TenantSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTenantSummary();
      setSummary(data);
    } catch (e) {
      setSummary(null);
      if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 404) {
        setError("NOT_FOUND");
      } else {
        setError(e instanceof Error ? e.message : "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const value: TenantSummaryContextValue = {
    summary,
    loading,
    error,
    refetch: fetchSummary,
  };

  return (
    <TenantSummaryContext.Provider value={value}>
      {children}
    </TenantSummaryContext.Provider>
  );
}

export function useTenantSummary(): TenantSummaryContextValue {
  const ctx = useContext(TenantSummaryContext);
  if (!ctx) {
    throw new Error("useTenantSummary must be used within TenantSummaryProvider");
  }
  return ctx;
}

export function useTenantSummaryOptional(): TenantSummaryContextValue | null {
  return useContext(TenantSummaryContext);
}
